import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Uso CFDI permitido por régimen fiscal del receptor (catálogo SAT c_UsoCFDI).
 * Fuente única: tabla `uso_cfdi_regimen` (248 pares); la BD también lo valida con trigger
 * en personas y receptores_factura. Con régimen 605/611/614/616 + G03 el PAC rechaza la
 * factura de mantenimiento sin avisar, por eso los selects solo ofrecen usos válidos.
 *
 * Si la tabla aún no existe en el ambiente (consulta con error), no se filtra nada.
 */
export function useUsoCfdiRegimen() {
  const { data: matriz, isSuccess } = useQuery({
    queryKey: ["uso_cfdi_regimen"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("uso_cfdi_regimen")
        .select("codigo_uso, id_regimen");
      if (error) throw error;
      const porRegimen = new Map<string, Set<string>>();
      for (const fila of (data ?? []) as { codigo_uso: string; id_regimen: string }[]) {
        const reg = fila.id_regimen.trim();
        if (!porRegimen.has(reg)) porRegimen.set(reg, new Set());
        porRegimen.get(reg)!.add(fila.codigo_uso.trim().toUpperCase());
      }
      return porRegimen;
    },
    staleTime: Infinity,
    retry: false,
  });

  /** true si el SAT permite el uso para el régimen; sin régimen, uso o matriz no se bloquea. */
  const esUsoPermitido = useCallback(
    (regimen: string | null | undefined, uso: string | null | undefined) => {
      const reg = regimen?.toString().trim();
      const cod = uso?.toString().trim().toUpperCase();
      if (!reg || !cod || !isSuccess || !matriz || matriz.size === 0) return true;
      return matriz.get(reg)?.has(cod) ?? false;
    },
    [matriz, isSuccess],
  );

  /** Deja solo los usos válidos para el régimen elegido (sin régimen, regresa la lista igual). */
  const filtrarUsos = useCallback(
    <T extends { codigo: string }>(usos: T[], regimen: string | null | undefined): T[] =>
      usos.filter((u) => esUsoPermitido(regimen, u.codigo)),
    [esUsoPermitido],
  );

  return useMemo(
    () => ({ esUsoPermitido, filtrarUsos, cargado: isSuccess }),
    [esUsoPermitido, filtrarUsos, isSuccess],
  );
}

/** Texto de error cuando el uso guardado no aplica al régimen. */
export const MENSAJE_USO_CFDI_NO_PERMITIDO =
  "Ese uso de CFDI no es válido para el régimen fiscal elegido. Elige uno permitido por el SAT (por ejemplo S01 - Sin efectos fiscales).";
